import { useLocation } from 'wouter';
import { ExternalLink, FlaskConical, Images, Settings2, Tags, Wand2, Activity } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/lib/auth-store';
import { canAccessPhotoChecksAdvanced } from '@/lib/photoChecksAccess';

/**
 * Advanced holds machine / lab controls that do not answer the four product questions:
 * attention, what we check, how well we know media, is it safe to make live.
 */
export default function PhotoChecksAdvancedPage() {
  const { state } = useAuth();
  const [, setLocation] = useLocation();
  const allowed = canAccessPhotoChecksAdvanced(state.user);

  if (!allowed) {
    return (
      <div className="container mx-auto p-6 max-w-3xl space-y-4">
        <h1 className="text-2xl font-semibold">Advanced</h1>
        <Alert variant="destructive">
          <AlertTitle>Specialist access required</AlertTitle>
          <AlertDescription>
            Lab and pipeline tools. Everyday decisions start from Home.
          </AlertDescription>
        </Alert>
        <Button onClick={() => setLocation('/admin/ai')}>Back to Home</Button>
      </div>
    );
  }

  const links = [
    {
      title: 'Prepare a trial',
      description: 'Lock reviewed photos and run live vs proposed comparisons. Decision surface is Try a change.',
      href: '/admin/ai/eval',
      icon: FlaskConical,
    },
    {
      title: 'Full standards editor',
      description: 'Versioned wording, models, and single-photo experiments.',
      href: '/admin/ai-triage/prompts',
      icon: Wand2,
    },
    {
      title: 'Format spotting (technical)',
      description: 'Mappings and specialist settings behind Know our media.',
      href: '/admin/ai/verification',
      icon: Settings2,
    },
    {
      title: 'Reviewed photos for trials',
      description: 'Source list used when building locked test sets.',
      href: '/admin/ai/gold-candidates',
      icon: Images,
    },
    {
      title: 'Teaching workspace',
      description: 'Mark examples that improve format spotting.',
      href: '/admin/ai/verification',
      icon: Tags,
      note: 'Open from Know our media → Improve from reviews when configured.',
    },
    {
      title: 'System status & queues',
      description: 'Pipeline health, retries, and diagnostics.',
      href: '/admin/ai/verification',
      icon: Activity,
      note: 'Use the System status tab on Know our media.',
    },
  ];

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-5xl">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-semibold">Advanced</h1>
          <Button variant="outline" size="sm" onClick={() => setLocation('/admin/ai')}>
            Home
          </Button>
        </div>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Controls that prepare or inspect the machine. Product screens answer business questions;
          open these only when the main workflow is not enough.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {links.map((link) => {
          const Icon = link.icon;
          return (
            <Card key={link.title}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className="h-4 w-4" />
                  {link.title}
                </CardTitle>
                <CardDescription>{link.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {link.note && <p className="text-xs text-muted-foreground">{link.note}</p>}
                <Button variant="outline" onClick={() => setLocation(link.href)}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
